import { Box, Grid, Typography } from '@material-ui/core'
import { CountrySelector } from 'components/Common/CountrySelector/CountrySelector'
import { RegionSelector } from 'components/Common/CountrySelector/RegionSelector'
import { AutoSaveWrapper } from 'components/Common/Form/Autosave'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import _ from 'lodash'
import { TextField } from 'mui-rff'
import React, { HTMLProps } from 'react'
import { Field, FieldMetaState, Form } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import {
  AddressDto,
  CountryDto,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { showFieldErrors } from 'utils/react-final-form-utils'
import './AddressForm.scss'

type Props = {
  address: AddressDto
  formTitle?: string
  variant: 'outlined' | 'filled' | 'standard'
  loading: boolean
  onChangeAddress: (address: AddressDto) => void | Promise<void>
  meta?: FieldMetaState<unknown>
} & HTMLProps<HTMLDivElement>

export const AddressForm = (props: Props) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { address, onChangeAddress, loading, formTitle, ...divProps } = props

  const { t } = useTranslation()

  const handleSubmit = (address: AddressDto) => {
    if (!_.isEqual(address, props.address)) {
      onChangeAddress(address)
    }
  }

  return (
    <div {...divProps} className={`${props.className}`}>
      {formTitle && (
        <Box marginBottom={2}>
          <Typography variant="h6">{formTitle}</Typography>
        </Box>
      )}
      <Form
        initialValues={address}
        onSubmit={handleSubmit}
        // validate={validateForm}
      >
        {(formProps) => {
          return (
            <div className="">
              <AutoSaveWrapper debounce={0} save={formProps.handleSubmit} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    name="street"
                    label={
                      <LocalizedTypography translationKey="common:address-street">
                        street
                      </LocalizedTypography>
                    }
                    disabled={props.loading}
                    variant={props.variant}
                    required={props.required}
                    autoComplete="street"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    name="houseNumber"
                    label={
                      <LocalizedTypography translationKey="common:address-house-number">
                        house number
                      </LocalizedTypography>
                    }
                    disabled={props.loading}
                    variant={props.variant}
                    required={props.required}
                    autoComplete="houseNumber"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    name="houseNumberAddition"
                    label={
                      <LocalizedTypography translationKey="common:address-house-number-addition">
                        addition
                      </LocalizedTypography>
                    }
                    disabled={props.loading}
                    variant={props.variant}
                    autoComplete="houseNumberAddition"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    name="zipCode"
                    label={
                      <LocalizedTypography translationKey="common:address-zipcode">
                        zip code
                      </LocalizedTypography>
                    }
                    variant={props.variant}
                    disabled={props.loading}
                    required={props.required}
                    autoComplete="zipCode"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Field<CountryDto> name="country">
                    {(fieldProps) => (
                      <CountrySelector
                        selectedCountryCode={fieldProps.input.value.code || ''}
                        onChange={(country) => {
                          fieldProps.input.onChange({
                            name: country.countryName,
                            code: country.countryShortCode,
                          })
                          formProps.form.change('stateOrProvince', undefined)
                          formProps.form.change('city', undefined)
                        }}
                        onBlur={fieldProps.input.onBlur}
                        onFocus={fieldProps.input.onFocus}
                        disabled={props.loading}
                        TextFieldProps={{
                          variant: props.variant,
                          required: props.required,
                          helperText: showFieldErrors(fieldProps.meta)
                            ? ''
                            : fieldProps.meta.error,
                          error: Boolean(fieldProps.meta.error),
                          label: t('common:country'),
                          disabled: props.loading,
                          name: 'country',
                          inputProps: {
                            autoComplete: 'país',
                          },
                        }}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Field<string> name="stateOrProvince">
                    {(fieldProps) => {
                      return (
                        <RegionSelector
                          countryCode={formProps.values.country?.code}
                          selectedRegionName={fieldProps.input.value}
                          onRegionChange={(region) =>
                            fieldProps.input.onChange(region?.name)
                          }
                          disabled={props.loading}
                          onFocus={fieldProps.input.onFocus}
                          onBlur={fieldProps.input.onBlur}
                          TextFieldProps={{
                            helperText: showFieldErrors(fieldProps.meta)
                              ? fieldProps.meta.error
                              : '',
                            error: showFieldErrors(fieldProps.meta),
                            label: t('common:state', 'state or province'),
                            variant: props.variant,
                            disabled: props.loading,
                            InputProps: {},
                            InputLabelProps: {
                              shrink: true,
                              disabled: props.loading,
                            },
                            inputProps: {
                              autoComplete: 'stateOrProvince',
                            },
                            name: 'stateOrProvince',
                          }}
                        />
                      )
                    }}
                  </Field>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    name="city"
                    label={
                      <LocalizedTypography translationKey="common:city">
                        city
                      </LocalizedTypography>
                    }
                    variant={props.variant}
                    disabled={props.loading}
                    autoComplete="city"
                  />
                </Grid>
              </Grid>
            </div>
          )
        }}
      </Form>
    </div>
  )
}
